<template lang="pug">
v-menu.suus__datepicker(
    v-model="menu"
    :close-on-content-click="false"
    :nudge-right="40"
    transition="scale-transition"
    offset-y
    min-width="auto"
)
    template(v-slot:activator="{ on, attrs }")
        v-text-field(
            ref="textField"
            placeholder="Datum auswählen"
            color="primary"
            filled
            persistent-placeholder
            clearable
            validate-on-blur
            background-color="lightGrey"
            v-bind="attrs"
            v-on="on"
            :value="formattedDisplayDate"
            :required="isMandatory"
            :error-messages="errorMessages"
            :rules="rules"
            @change="handleTextInput"
            @click:clear="onClear"
            @click="togglePicker"
        ).suus__text-field
            template(v-slot:prepend)
                v-icon(color="primary") {{ calenderIcon }}
            template(v-slot:label)
                span(:style="labelStyles") {{ label }}
                span(v-if="isMandatory" :style="mandatoryStyles") *
    v-date-picker(
        locale="de-de"
        scrollable
        v-model="date"
        elevation="15"
        :min="minDate"
        :max="maxDate"
        :first-day-of-week="1"
        :allowed-dates="allowedDates"
        @change="onInput"
        :picker-date.sync="computedPickerDate"
    )
</template>

<script lang="ts">
import Vue, { computed, PropType, ref, watch } from "vue"
import { mdiCalendarRange } from "@mdi/js"
import moment from "moment"

export default Vue.extend({
  name: "DatePicker",
  data: function () {
    return {
      date: this.value,
      menu: false,
      calenderIcon: mdiCalendarRange,
    }
  },
  props: {
    value: { type: String },
    pickerDate: { type: String },
    label: { type: String, default: "Datum" },
    isMandatory: { type: Boolean, default: true },
    minDate: { type: String, default: undefined },
    maxDate: { type: String, default: undefined },
    allowedDates: {
      type: Function as PropType<(string) => boolean>,
      default: (_) => true,
    },
  },
  computed: {
    formattedDisplayDate(): string | null {
      return this.date ? moment(this.date).format("dd. DD.MM.YYYY") : null
    },
    labelStyles(): object {
      return {
        color: this.$vuetify.theme.themes.light.primary,
        fontWeight: 400,
      }
    },
    mandatoryStyles(): object {
      return {
        color: "red",
        fontWeight: 400,
      }
    },
    errorMessages() {
      if (!this.date && this.isMandatory) {
        return ["Bitte wähle einen Datum."]
      }
      return []
    },
  },
  methods: {
    onInput(val: string): void {
      this.menu = false
      if (this.errorMessages.length === 0) {
        this.$emit("input", val)
      }
      this.$nextTick(() => {
        // Force validation on the v-text-field
        ;(this.$refs.textField as any).validate()
      })
    },
    togglePicker(): void {
      this.menu = !this.menu
    },
    onClear(): void {
      this.$emit("input", null)
    },
    handleTextInput(val: string): void {
      const date = moment(
        val,
        ["dd. DD.MM.YYYY", "dd. DD.MM.YY", "DD.MM.YYYY", "DD.MM.YY"],
        false
      )
      if (date.isValid()) {
        this.date = date.format("YYYY-MM-DD")
      }
    },
  },
  watch: {
    value(val: string): void {
      if (val !== this.date) this.date = val
    },
  },
  setup(props) {
    const pickerDateNew = ref(undefined)

    const computedPickerDate = computed({
      get() {
        return pickerDateNew.value
      },
      set(value: string) {
        if (value !== pickerDateNew.value)
          pickerDateNew.value = value ? moment(value).format("YYYY-MM") : null
      },
    })

    watch(
      () => props.pickerDate,
      (val) => {
        pickerDateNew.value = val
      },
      { immediate: true }
    )

    const rules = [
      (v: string) => {
        if (!v) return true
        const date = moment(
          v,
          ["dd. DD.MM.YYYY", "dd. DD.MM.YY", "DD.MM.YYYY", "DD.MM.YY"],
          false
        )
        return date.isValid() || "Das gültige Format ist DD.MM.YYYY"
      },
    ]

    return {
      rules,
      computedPickerDate,
    }
  },
})
</script>

<style lang="sass">
%font-stack
    font-family: system-ui !important
.suus__datepicker
    @extend %font-stack
.suus__text-field
    @extend %font-stack
    .v-input__slot
        .v-text-field__slot
            input
                margin-top: 24px !important
                @extend %font-stack
                font-size: 1em !important
</style>
